import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import NameCollectionModal from '../components/NameCollectionModal';
import axios from 'axios';

const LandingPage = () => {
  const navigate = useNavigate();
  const [showNameModal, setShowNameModal] = useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    loadQuestionnaires();
  }, []);

  const loadQuestionnaires = async () => {
    try {
      const apiUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:7071/api/getQuestionnaireTypes'
        : '/api/getQuestionnaireTypes';

      const response = await axios.get(apiUrl, {
        params: { activeOnly: 'true' },
        withCredentials: true,
      });

      if (response.data.success) {
        // Convert object to array and sort by totalQuestions
        const questionnaireArray = Object.values(response.data.questionnaireTypes);
        const sortedQuestionnaires = questionnaireArray.sort((a, b) => a.totalQuestions - b.totalQuestions);
        setQuestionnaires(sortedQuestionnaires);
      } else {
        throw new Error('Failed to load questionnaires');
      }
    } catch (error) {
      console.error('Error loading questionnaires:', error);
      setError('Erreur lors du chargement des questionnaires');
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionnaireClick = (questionnaire) => {
    setSelectedQuestionnaire(questionnaire);
    setShowNameModal(true);
  };

  const handleNameSubmit = async (userName) => {
    try {
      // Store the userName in localStorage for use throughout the session
      localStorage.setItem('currentUserName', userName);
      
      // Navigate to the selected questionnaire
      navigate(`/questionnaire/${selectedQuestionnaire.id}`);
    } catch (error) {
      console.error('Error storing user name:', error);
      throw error;
    }
  };

  const handleModalClose = () => {
    setShowNameModal(false);
    setSelectedQuestionnaire(null);
  };

  if (loading) {
    return (
      <div className="container" style={{ marginTop: '2rem' }}>
        <div className="loading">Chargement des questionnaires...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ marginTop: '2rem' }}>
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="container" style={{ marginTop: '2rem' }}>
      {/* Development Links */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '1rem',
          padding: '0.5rem',
          backgroundColor: '#fff3cd',
          borderRadius: '5px',
          border: '1px solid #ffeaa7'
        }}>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/admin" 
              style={{ 
                color: '#856404',
                textDecoration: 'none',
                fontWeight: 'bold',
                fontSize: '0.9rem'
              }}
            >
              🔧 Tableau de Bord Admin
            </Link>
            <Link 
              to="/test-results" 
              style={{ 
                color: '#856404',
                textDecoration: 'none',
                fontWeight: 'bold',
                fontSize: '0.9rem'
              }}
            >
              🧪 Test Rapports & PDFs
            </Link>
          </div>
        </div>
      )}
      
      <div className="card">
        <h1 style={{ textAlign: 'center', marginBottom: '2rem', color: '#2c3e50' }}>
          Bienvenue dans l'espace des Questionnaires
        </h1>
        <p style={{ textAlign: 'center', fontSize: '1.2rem', marginBottom: '2rem', color: '#7f8c8d' }}>
          Explorez vos dons et votre personnalité grâce à nos questionnaires spécialisés
        </p>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            padding: '1.25rem 1.5rem',
            marginBottom: '2.5rem',
            background: 'linear-gradient(135deg, #8e44ad 0%, #3498db 100%)',
            borderRadius: 10,
            color: 'white'
          }}
        >
          <div>
            <div style={{ fontSize: '1.05rem', fontWeight: 600 }}>Exercice en direct (animé)</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>
              Rejoindre une session en cours ou en animer une (24 questions, anonyme, en groupe).
            </div>
          </div>
          <Link
            to="/live"
            style={{
              background: 'white',
              color: '#3498db',
              padding: '0.65rem 1.25rem',
              borderRadius: 6,
              textDecoration: 'none',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}
          >
            Aller à la session →
          </Link>
        </div>

        <div className="questionnaires-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem' }}>
          {questionnaires.map((questionnaire) => (
            <div key={questionnaire.id} className="questionnaire-card card" style={{ 
              border: `3px solid ${questionnaire.color}`,
              position: 'relative'
            }}>
              <div style={{ 
                position: 'absolute', 
                top: '-10px', 
                right: '20px', 
                backgroundColor: questionnaire.color,
                color: 'white',
                padding: '5px 15px',
                borderRadius: '15px',
                fontSize: '0.8rem',
                fontWeight: 'bold'
              }}>
                {questionnaire.type === 'spiritual-gifts' 
                  ? questionnaire.totalQuestions <= 84 ? 'RAPIDE' : 'COMPLET'
                  : 'SPÉCIALISÉ'
                }
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <span style={{ fontSize: '2rem', marginRight: '1rem' }}>
                  {questionnaire.icon}
                </span>
                <h2 style={{ color: '#2c3e50', margin: 0, fontSize: '1.2rem' }}>
                  {questionnaire.name}
                </h2>
              </div>
              
              <p style={{ marginBottom: '1.5rem', lineHeight: '1.6' }}>
                {questionnaire.description}
              </p>
              
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c3e50' }}>
                      {questionnaire.totalQuestions}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>Questions</div>
                  </div>
                  {questionnaire.categories && (
                    <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c3e50' }}>
                        {questionnaire.categories}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>Catégories</div>
                    </div>
                  )}
                  <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#2c3e50' }}>
                      {questionnaire.duration}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>Durée</div>
                  </div>
                </div>
              </div>
              
              <button 
                onClick={() => handleQuestionnaireClick(questionnaire)}
                className="btn"
                style={{ 
                  width: '100%',
                  backgroundColor: questionnaire.color,
                  color: 'white',
                  fontSize: '1.1rem',
                  padding: '1rem',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease'
                }}
                onMouseOver={(e) => {
                  // Darken the color on hover
                  const originalColor = questionnaire.color;
                  e.target.style.backgroundColor = `${originalColor}CC`;
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = questionnaire.color;
                }}
              >
                {questionnaire.icon} Commencer le questionnaire
              </button>
              
              {/* Dynamic tip based on questionnaire type */}
              <div style={{ 
                marginTop: '1rem', 
                padding: '1rem', 
                backgroundColor: `${questionnaire.color}15`, 
                borderRadius: '8px',
                fontSize: '0.9rem',
                color: questionnaire.color
              }}>
                💡 <strong>Type :</strong> {
                  questionnaire.type === 'spiritual-gifts' 
                    ? 'Découverte des dons spirituels'
                    : questionnaire.type === 'orientation-conscience'
                    ? 'Analyse psychologique des orientations de conscience'
                    : questionnaire.type
                }
              </div>
            </div>
          ))}
        </div>
        
        <div style={{ marginTop: '3rem', textAlign: 'center' }}>
          <h3 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
            Comment ça marche ?
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem', marginTop: '2rem' }}>
            <div>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>1️⃣</div>
              <h4>Choisissez votre questionnaire</h4>
              <p>Sélectionnez le questionnaire qui vous intéresse selon vos objectifs</p>
            </div>
            <div>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>2️⃣</div>
              <h4>Répondez honnêtement</h4>
              <p>Évaluez chaque affirmation selon l'échelle proposée</p>
            </div>
            <div>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>3️⃣</div>
              <h4>Découvrez vos résultats</h4>
              <p>Visualisez vos profils sous forme de graphiques et recevez un rapport PDF</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Version footer */}
      <footer style={{ 
        textAlign: 'center', 
        marginTop: '3rem', 
        padding: '2rem 0', 
        borderTop: '1px solid #e9ecef',
        fontSize: '0.85rem', 
        color: '#6c757d' 
      }}>
        <div>
          Version v1.0.87 - 28 mai 2026, 08:35
        </div>
        <div style={{ marginTop: '0.5rem' }}>
          © 2025 - Tous droits réservés
        </div>
      </footer>

      <NameCollectionModal
        isOpen={showNameModal}
        onSubmit={handleNameSubmit}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default LandingPage;
